import useMap from "./useMap";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import {
    FIELD_MAP_CENTERS_CLUSTER_COUNT_LAYER,
    FIELD_MAP_CENTERS_CLUSTER_LAYER,
    FIELD_MAP_CENTERS_SOURCE,
    FIELD_MAP_CENTERS_UNCLUSTERED_LAYER,
    FIELD_MAP_TILES_FILL_LAYER,
    FIELD_MAP_TILES_LINE_LAYER,
    FIELD_MAP_TILES_SOURCE,
} from "./layers";
import { FeatureCollection, Point } from "geojson";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    buildFieldMapTilesUrl,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "../utils/map";
import { getApiBaseUrl } from "@/lib/request/RequestProvider";

import mapboxgl, {
    CircleLayerSpecification,
    FillLayerSpecification,
    GeoJSONSource,
    LineLayerSpecification,
    LngLatBoundsLike,
    MapMouseEvent,
    Marker,
    SymbolLayerSpecification,
} from "mapbox-gl";
import { useMatch, useNavigate } from "@tanstack/react-router";
import ImageryTimelineV2 from "../../components/field/ImageryTimelineV2";
import useRequest from "../request/useRequest";
import LayersMenu from "@/components/field/layers/LayersMenu";
import { useQuery } from "@tanstack/react-query";
import * as turf from "@turf/turf";
import { debounce } from "lodash";

type MapCenterProperties = {
    id: number;
    grower: number;
    marker_color: string;
};

const EMPTY_CENTERS: FeatureCollection<Point, MapCenterProperties> = {
    type: "FeatureCollection",
    features: [],
};

const CLUSTER_MAX_ZOOM = 8;
const CLUSTER_RADIUS = 28;
const CLUSTER_CONFIG_KEY = `${CLUSTER_MAX_ZOOM}:${CLUSTER_RADIUS}`;
const MARKER_HIT_SIZE_PX = 64;
const POLYGON_MIN_ZOOM = 10;

function fieldInsightsHref(fieldId: string) {
    return `/dashboard/fields/${fieldId}/insights/ntime`;
}

const MARKER_COLOR_MATCH = [
    "match",
    ["get", "marker_color"],
    "red",
    "#ef4444",
    "green",
    "#22c55e",
    "gray",
    "#9ca3af",
    "yellow",
    "#eab308",
    "white",
    "#ffffff",
    "#808080",
] as const;

const CLUSTER_LAYER: CircleLayerSpecification = {
    id: FIELD_MAP_CENTERS_CLUSTER_LAYER,
    source: FIELD_MAP_CENTERS_SOURCE,
    type: "circle",
    filter: ["has", "point_count"],
    paint: {
        "circle-color": "#09426A",
        "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26],
        "circle-opacity": 0.9,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
    },
};

const CLUSTER_COUNT_LAYER: SymbolLayerSpecification = {
    id: FIELD_MAP_CENTERS_CLUSTER_COUNT_LAYER,
    source: FIELD_MAP_CENTERS_SOURCE,
    type: "symbol",
    filter: ["has", "point_count"],
    layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-size": 12,
        "text-allow-overlap": true,
    },
    paint: {
        "text-color": "#ffffff",
    },
};

const UNCLUSTERED_LAYER: SymbolLayerSpecification = {
    id: FIELD_MAP_CENTERS_UNCLUSTERED_LAYER,
    source: FIELD_MAP_CENTERS_SOURCE,
    type: "symbol",
    filter: ["!", ["has", "point_count"]],
    layout: {
        "symbol-placement": "point",
        "icon-allow-overlap": true,
        "icon-image": [
            "case",
            ["==", "red", ["get", "marker_color"]],
            "location-red",
            ["==", "green", ["get", "marker_color"]],
            "location-green",
            ["==", "gray", ["get", "marker_color"]],
            "location-gray",
            ["==", "yellow", ["get", "marker_color"]],
            "location-yellow",
            "location-white",
        ],
        "icon-anchor": "bottom",
        "icon-size": 0.08,
    },
};

const FILL_LAYER: FillLayerSpecification = {
    id: FIELD_MAP_TILES_FILL_LAYER,
    source: FIELD_MAP_TILES_SOURCE,
    "source-layer": "fields",
    type: "fill",
    minzoom: POLYGON_MIN_ZOOM,
    paint: {
        "fill-color": [...MARKER_COLOR_MATCH],
        "fill-opacity": 0.35,
    },
};

const LINE_LAYER: LineLayerSpecification = {
    id: FIELD_MAP_TILES_LINE_LAYER,
    source: FIELD_MAP_TILES_SOURCE,
    "source-layer": "fields",
    type: "line",
    minzoom: POLYGON_MIN_ZOOM,
    paint: {
        "line-color": [...MARKER_COLOR_MATCH],
        "line-width": 2,
        "line-opacity": 0.9,
    },
};

const CENTER_LAYER_IDS = [
    CLUSTER_LAYER.id,
    CLUSTER_COUNT_LAYER.id,
    UNCLUSTERED_LAYER.id,
];
const TILE_LAYER_IDS = [FILL_LAYER.id, LINE_LAYER.id];

function BaseMap() {
    const { axiosRequest } = useRequest();
    const navigate = useNavigate();
    const { mapContainerRef, map, mapLoaded, initializeMap } = useMap();
    const hasFitBounds = useRef(false);
    const appliedClusterConfig = useRef<string | null>(null);
    const appliedCentersFilters = useRef<Record<
        string,
        string | number
    > | null>(null);
    const appliedTileUrl = useRef<string | null>(null);
    const fieldLinkMarkers = useRef(new Map<string, Marker>());

    const match = useMatch({
        from: "/dashboard/fields/$fieldId",
        shouldThrow: false,
    });

    const matchFields = useMatch({
        from: "/dashboard/fields/",
        shouldThrow: false,
    });

    const fieldListFilters = useMemo(() => {
        const search = matchFields?.search;
        if (!search) return {};
        return Object.fromEntries(
            Object.entries({
                search: search.search,
                service_provider: search.service_provider,
                grower: search.grower,
                account_manager: search.account_manager,
                enrollment_tier: search.enrollment_tier,
            }).filter(([, value]) => value != null && value !== ""),
        ) as Record<string, string | number>;
    }, [matchFields?.search]);

    const tilesUrl = useMemo(
        () => buildFieldMapTilesUrl(getApiBaseUrl(), fieldListFilters),
        [fieldListFilters],
    );

    const { data: centersData } = useQuery<
        FeatureCollection<Point, MapCenterProperties>
    >({
        queryKey: ["fields/map-centers/", fieldListFilters],
        queryFn: () =>
            axiosRequest<FeatureCollection<Point, MapCenterProperties>>({
                url: "fields/map-centers/",
                params: fieldListFilters,
            }),
        enabled: mapLoaded && !!matchFields,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        staleTime: 60_000,
    });

    const markerData = useMemo(() => {
        const features = centersData?.features?.filter((f) => !!f.geometry) ?? [];
        return turf.featureCollection(
            features.map((f) => ({
                ...f,
                id: f.id ?? f.properties?.id,
            })),
        ) as FeatureCollection<Point, MapCenterProperties>;
    }, [centersData?.features]);

    useEffect(() => {
        if (mapContainerRef) {
            initializeMap();
        }
    }, [initializeMap, mapContainerRef]);

    useEffect(() => {
        if (!map || !mapLoaded) return;

        if (!matchFields || match) {
            removeLayersIfExists(map, CENTER_LAYER_IDS);
            removeSourcesIfExists(map, [FIELD_MAP_CENTERS_SOURCE]);
            hasFitBounds.current = false;
            appliedClusterConfig.current = null;
            appliedCentersFilters.current = null;
            return;
        }

        if (appliedCentersFilters.current !== fieldListFilters) {
            appliedCentersFilters.current = fieldListFilters;
            hasFitBounds.current = false;
        }

        // Clear stale pins while the filtered query is in flight
        if (!centersData) {
            const existing = map.getSource(
                FIELD_MAP_CENTERS_SOURCE,
            ) as GeoJSONSource | undefined;
            if (existing) {
                existing.setData(EMPTY_CENTERS);
            }
            return;
        }

        if (
            map.getSource(FIELD_MAP_CENTERS_SOURCE) &&
            appliedClusterConfig.current !== CLUSTER_CONFIG_KEY
        ) {
            removeLayersIfExists(map, CENTER_LAYER_IDS);
            removeSourcesIfExists(map, [FIELD_MAP_CENTERS_SOURCE]);
        }

        addUpdateGeojsonSource(map, FIELD_MAP_CENTERS_SOURCE, {
            type: "geojson",
            data: markerData,
            cluster: true,
            clusterMaxZoom: CLUSTER_MAX_ZOOM,
            clusterRadius: CLUSTER_RADIUS,
        });
        appliedClusterConfig.current = CLUSTER_CONFIG_KEY;
        addUpdateGeojsonLayers(map, [
            CLUSTER_LAYER,
            CLUSTER_COUNT_LAYER,
            UNCLUSTERED_LAYER,
        ]);

        if (!hasFitBounds.current && markerData.features.length) {
            const boundsFeatures = markerData.features.filter(
                (feature) => feature.properties.grower !== 1,
            );
            if (boundsFeatures.length) {
                map.fitBounds(
                    turf.bbox(
                        turf.featureCollection(boundsFeatures),
                    ) as LngLatBoundsLike,
                    {
                        padding: 80,
                        duration: 0,
                        maxZoom: 14,
                    },
                );
                hasFitBounds.current = true;
            }
        }
    }, [
        centersData,
        fieldListFilters,
        map,
        mapLoaded,
        markerData,
        match,
        matchFields,
    ]);

    // MVT polygons (colored fields)
    useEffect(() => {
        if (!map || !mapLoaded) return;

        if (!matchFields || match) {
            removeLayersIfExists(map, TILE_LAYER_IDS);
            removeSourcesIfExists(map, [FIELD_MAP_TILES_SOURCE]);
            appliedTileUrl.current = null;
            return;
        }

        if (
            map.getSource(FIELD_MAP_TILES_SOURCE) &&
            appliedTileUrl.current !== tilesUrl
        ) {
            removeLayersIfExists(map, TILE_LAYER_IDS);
            removeSourcesIfExists(map, [FIELD_MAP_TILES_SOURCE]);
        }

        if (!map.getSource(FIELD_MAP_TILES_SOURCE)) {
            map.addSource(FIELD_MAP_TILES_SOURCE, {
                type: "vector",
                tiles: [tilesUrl],
                minzoom: POLYGON_MIN_ZOOM,
                maxzoom: 16,
                promoteId: {
                    fields: "id",
                },
            });
            appliedTileUrl.current = tilesUrl;
        }

        if (!map.getLayer(FILL_LAYER.id)) {
            // Under pin layers so markers stay on top
            const beforeId = map.getLayer(CLUSTER_LAYER.id)
                ? CLUSTER_LAYER.id
                : map.getLayer(UNCLUSTERED_LAYER.id)
                    ? UNCLUSTERED_LAYER.id
                    : undefined;
            map.addLayer(FILL_LAYER, beforeId);
        }
        if (!map.getLayer(LINE_LAYER.id)) {
            const beforeId = map.getLayer(CLUSTER_LAYER.id)
                ? CLUSTER_LAYER.id
                : map.getLayer(UNCLUSTERED_LAYER.id)
                    ? UNCLUSTERED_LAYER.id
                    : undefined;
            map.addLayer(LINE_LAYER, beforeId);
        }
    }, [map, mapLoaded, match, matchFields, tilesUrl]);

    const clearFieldLinkMarkers = useCallback(() => {
        for (const marker of fieldLinkMarkers.current.values()) {
            marker.remove();
        }
        fieldLinkMarkers.current.clear();
    }, []);

    const syncFieldLinkMarkers = useCallback(() => {
        if (!map || !mapLoaded || !matchFields || match) {
            clearFieldLinkMarkers();
            return;
        }
        if (!map.getLayer(UNCLUSTERED_LAYER.id)) {
            clearFieldLinkMarkers();
            return;
        }

        const features = map.queryRenderedFeatures({
            layers: [UNCLUSTERED_LAYER.id],
        });
        const seen = new Set<string>();

        for (const feature of features) {
            const fieldId = String(feature.id ?? feature.properties?.id ?? "");
            if (!fieldId || seen.has(fieldId)) continue;
            seen.add(fieldId);

            const geometry = feature.geometry as Point;
            const lngLat = geometry.coordinates as [number, number];
            const existing = fieldLinkMarkers.current.get(fieldId);
            if (existing) {
                existing.setLngLat(lngLat);
                continue;
            }

            const el = document.createElement("a");
            el.href = fieldInsightsHref(fieldId);
            el.setAttribute("aria-label", `Open field ${fieldId}`);
            el.style.width = `${MARKER_HIT_SIZE_PX}px`;
            el.style.height = `${MARKER_HIT_SIZE_PX}px`;
            el.style.display = "block";
            el.style.cursor = "pointer";
            el.addEventListener("click", (event) => {
                if (
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                ) {
                    return;
                }
                event.preventDefault();
                navigate({
                    to: "/dashboard/fields/$fieldId/insights/ntime",
                    params: { fieldId },
                    search: {
                        advisor: undefined,
                    },
                });
            });

            const marker = new mapboxgl.Marker({
                element: el,
                anchor: "bottom",
            })
                .setLngLat(lngLat)
                .addTo(map);
            fieldLinkMarkers.current.set(fieldId, marker);
        }

        for (const [fieldId, marker] of fieldLinkMarkers.current) {
            if (!seen.has(fieldId)) {
                marker.remove();
                fieldLinkMarkers.current.delete(fieldId);
            }
        }
    }, [clearFieldLinkMarkers, map, mapLoaded, match, matchFields, navigate]);

    useEffect(() => {
        if (!map || !mapLoaded) return;

        const debouncedSync = debounce(syncFieldLinkMarkers, 50);
        map.on("idle", debouncedSync);
        debouncedSync();
        return () => {
            map.off("idle", debouncedSync);
            debouncedSync.cancel();
            clearFieldLinkMarkers();
        };
    }, [clearFieldLinkMarkers, map, mapLoaded, syncFieldLinkMarkers]);

    const addingField = useMatch({
        from: "/dashboard/fields/add",
        shouldThrow: false,
    });

    const addingBulkField = useMatch({
        from: "/dashboard/fields/add-bulk",
        shouldThrow: false,
    });

    const clickHandler = useCallback(
        (e: MapMouseEvent) => {
            if (!map) return;

            const clusterFeatures = map.queryRenderedFeatures(e.point, {
                layers: [CLUSTER_LAYER.id],
            });
            if (clusterFeatures.length) {
                const clusterId = clusterFeatures[0].properties
                    ?.cluster_id as number;
                const source = map.getSource(
                    FIELD_MAP_CENTERS_SOURCE,
                ) as GeoJSONSource;
                source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err || zoom == null || !map) return;
                    const geometry = clusterFeatures[0].geometry as Point;
                    map.easeTo({
                        center: geometry.coordinates as [number, number],
                        zoom,
                    });
                });
                return;
            }

            const features = map.queryRenderedFeatures(e.point, {
                layers: [FILL_LAYER.id, LINE_LAYER.id, UNCLUSTERED_LAYER.id],
            });
            if (!features.length) return;

            const fieldId = String(
                features[0].id ?? features[0].properties?.id ?? "",
            );
            if (!fieldId) return;

            navigate({
                to: "/dashboard/fields/$fieldId/insights/ntime",
                params: { fieldId },
                search: {
                    advisor: undefined,
                },
            });
        },
        [map, navigate],
    );

    useEffect(() => {
        if (!map || !mapLoaded) return;

        const interactiveLayers = [
            CLUSTER_LAYER.id,
            FILL_LAYER.id,
            LINE_LAYER.id,
            UNCLUSTERED_LAYER.id,
        ];
        const onEnter = () => {
            map.getCanvas().style.cursor = "pointer";
        };
        const onLeave = () => {
            map.getCanvas().style.cursor = "";
        };

        if (!match && !addingField && !addingBulkField && matchFields) {
            map.on("click", clickHandler);
            interactiveLayers.forEach((layerId) => {
                map.on("mouseenter", layerId, onEnter);
                map.on("mouseleave", layerId, onLeave);
            });
        }

        return () => {
            map.off("click", clickHandler);
            interactiveLayers.forEach((layerId) => {
                map.off("mouseenter", layerId, onEnter);
                map.off("mouseleave", layerId, onLeave);
            });
        };
    }, [
        addingField,
        addingBulkField,
        clickHandler,
        map,
        mapLoaded,
        match,
        matchFields,
    ]);

    return (
        <div className="flex flex-col w-full h-full">
            <div className="relative flex-1 min-h-0">
                <div
                    id="map"
                    ref={mapContainerRef}
                    className="bg-gray-200 w-full h-full"
                />
                {match && <LayersMenu />}
            </div>
            {match && (
                <div className="shrink-0 w-full bg-white border-t border-l border-default-200 flex flex-row gap-3 items-stretch">
                    <ImageryTimelineV2 />
                </div>
            )}
        </div>
    );
}
export default memo(BaseMap);
